/**
 * Owns the single AudioContext the app uses.
 *
 * An AudioContext is a capped browser resource: Safari allows four live at
 * once and the fifth constructor call throws, and a context that is abandoned
 * without being closed keeps counting against the cap for the life of the
 * page. The app used to build one per feature — the readiness probe, the mic
 * level meter, the speaker test tone, the talk track, the local speech
 * analyser — so opening the device dialog a handful of times could exhaust the
 * cap and take audio down for the rest of the session.
 *
 * None of those features needs a context of its own. One context mixes any
 * number of independent node graphs, so they are multiplexed onto a single
 * shared one. Consumers take a lease, build their nodes on `lease.context`,
 * and release the lease when they are done; releasing tears down the
 * consumer's own nodes, never the shared context, so no consumer can pull
 * audio out from under another. The context is created on first use and then
 * lives for the life of the page — recreating it is the churn this replaces.
 */

type AudioContextConstructor = new (
  options?: AudioContextOptions
) => AudioContext

export type AudioContextLease = {
  readonly context: AudioContext
  /** Idempotent — releasing an already-released lease does nothing. */
  release(): void
}

type StateListener = (state: AudioContextState) => void

function getAudioContextConstructor(): AudioContextConstructor | null {
  if (typeof window === 'undefined') return null
  const w = window as typeof globalThis & {
    webkitAudioContext?: AudioContextConstructor
  }
  return w.AudioContext ?? w.webkitAudioContext ?? null
}

/**
 * Read through a call so the compiler can't narrow `state` from an earlier
 * comparison — it changes on its own while we await a resume.
 */
function isRunning(context: AudioContext): boolean {
  return context.state === 'running'
}

type AudioContextWithSink = AudioContext & {
  setSinkId: (id: string) => Promise<void>
}

/** Safari has no AudioContext.setSinkId, so output routing is Chrome-only. */
function supportsSinkId(
  context: AudioContext
): context is AudioContextWithSink {
  return 'setSinkId' in context
}

export class AudioContextController {
  private context: AudioContext | null = null
  /** Lease id -> owner label, so live holders are visible while debugging. */
  private leases = new Map<symbol, string>()
  private workletModules = new Map<string, Promise<void>>()
  private listeners = new Set<StateListener>()
  /** Last device asked for, so a context built later still lands on it. */
  private sinkId: string | null = null

  get isSupported(): boolean {
    return getAudioContextConstructor() !== null
  }

  /** `'unavailable'` when no context has been created yet, or none can be. */
  get state(): AudioContextState | 'unavailable' {
    return this.context?.state ?? 'unavailable'
  }

  /** Owner labels of every lease currently held, for diagnostics. */
  get activeLeases(): string[] {
    return [...this.leases.values()]
  }

  /**
   * Take a lease on the shared context. Returns null when the browser has no
   * AudioContext, or when constructing it failed — callers decide whether that
   * is fatal or simply means no audio analysis.
   */
  acquire(owner: string): AudioContextLease | null {
    const context = this.ensureContext()
    if (!context) return null

    const id = Symbol(owner)
    this.leases.set(id, owner)

    // A suspended context produces silence rather than an error, so nudge it on
    // every acquire. Rejects outside a user gesture, which is the expected case
    // before audio has been unlocked; readiness is tracked separately.
    if (context.state === 'suspended') void context.resume().catch(() => {})

    let released = false
    return {
      context,
      release: () => {
        if (released) return
        released = true
        this.leases.delete(id)
      },
    }
  }

  /**
   * Resume the shared context, creating it if needed, and report whether it is
   * running. Callers use this to decide whether audio has been unlocked; it
   * takes no lease because it builds no nodes.
   */
  async ensureRunning(): Promise<boolean> {
    const context = this.ensureContext()
    if (!context) return false
    if (isRunning(context)) return true
    // Rejects when no user gesture has unlocked audio yet. The caller retries
    // from a click handler, and the state listener catches an unlock that
    // lands after this resolves.
    await context.resume().catch(() => {})
    return isRunning(context)
  }

  /**
   * Load an AudioWorklet module onto the shared context, at most once per URL.
   *
   * `addModule` re-executes the module script in the same
   * AudioWorkletGlobalScope, and `registerProcessor` throws on a name that is
   * already registered — so a second consumer of the same worklet has to reuse
   * the first load rather than repeat it.
   */
  loadWorklet(url: string): Promise<void> {
    const context = this.ensureContext()
    if (!context) {
      return Promise.reject(
        new Error('Audio Context not supported on this browser')
      )
    }

    const inFlight = this.workletModules.get(url)
    if (inFlight) return inFlight

    const loading = context.audioWorklet.addModule(url).catch((e) => {
      // A failed load must not stay cached, or every later attempt inherits the
      // same rejection.
      this.workletModules.delete(url)
      throw e
    })
    this.workletModules.set(url, loading)
    return loading
  }

  /**
   * Route the shared context's output to a specific device.
   *
   * The whole app mixes into one context, so this is the app's output device,
   * not one feature's — the device picker is what should call it. Remembered
   * so a context created after the choice still honours it. No-op where
   * unsupported; rejects when the device is gone, which the caller handles.
   *
   * The device has to be remembered before the call, not after, because there
   * may be no context to call on yet — that is the case the memory exists for.
   * A rejection puts the previous device back, so a context built later is not
   * routed to one we already know is gone.
   *
   * A closed context counts as no context to call on. This reads `this.context`
   * rather than building one, so it sees the dead context from the moment the
   * browser closes it until something else replaces it, and `setSinkId` on a
   * closed context rejects. That rejection says nothing about the device, but
   * the catch below cannot tell it from a device that is gone — it would put
   * the previous device back and route the replacement context to the one the
   * user just switched away from.
   */
  async setSinkId(deviceId: string): Promise<void> {
    const previous = this.sinkId
    this.sinkId = deviceId
    const context = this.context
    if (!context || context.state === 'closed' || !supportsSinkId(context)) {
      return
    }
    try {
      await context.setSinkId(deviceId)
    } catch (e) {
      // Only undo our own write. A call that started while this one was
      // awaiting has already replaced the memory, and restoring `previous` over
      // it would point a context built later at a device the user has since
      // switched away from.
      if (this.sinkId === deviceId) this.sinkId = previous
      throw e
    }
  }

  /** Observe state changes on the shared context. Returns an unsubscribe. */
  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * Close the shared context and forget every cached load. Only for teardown in
   * tests — production code releases leases and leaves the context alive.
   */
  async reset(): Promise<void> {
    const context = this.context
    this.context = null
    this.leases.clear()
    this.workletModules.clear()
    this.sinkId = null
    if (!context) return
    context.removeEventListener('statechange', this.handleStateChange)
    if (context.state === 'closed') return
    await context.close().catch(() => {})
  }

  private ensureContext(): AudioContext | null {
    if (this.context && this.context.state !== 'closed') return this.context

    const Ctor = getAudioContextConstructor()
    if (!Ctor) return null

    // Retire the context being replaced. The listener goes so a late
    // statechange from it can't be reported as the live context's state, and
    // the worklet cache goes because modules are registered per context — both
    // belong to the dead context whether or not a replacement gets built.
    this.context?.removeEventListener('statechange', this.handleStateChange)
    this.workletModules.clear()

    try {
      const context = new Ctor({ latencyHint: 'interactive' })
      context.addEventListener('statechange', this.handleStateChange)
      this.context = context
      // A device picked before this context existed still applies to it.
      if (this.sinkId && supportsSinkId(context)) {
        void context.setSinkId(this.sinkId).catch(() => {})
      }
      return context
    } catch {
      // The constructor throws when the browser's live-context cap is already
      // exhausted. Nothing here can recover that, and callers all have a
      // no-audio path, so report the failure rather than crashing them.
      //
      // Dropping the context being replaced is what makes `state` say
      // `'unavailable'` here rather than `'closed'`: there is no live context
      // and none can be built. The replaced one is already closed — this
      // branch is only reachable with a closed context or none at all — and
      // its listener and worklets went above, so nothing is left to keep it
      // for.
      this.context = null
      return null
    }
  }

  private handleStateChange = () => {
    const context = this.context
    if (!context) return
    const state = context.state

    // A context that suspends on its own — tab backgrounded, OS audio
    // interruption — produces silence until something resumes it, and nothing
    // else will: the app only resumes on `acquire` and `ensureRunning`, and
    // neither runs again while a meeting is already up. Rejects when the
    // browser wants a fresh user gesture, which leaves the context suspended
    // rather than making it worse; `ensureRunning` from a click is the way out
    // of that one.
    if (state === 'suspended') void context.resume().catch(() => {})

    // iOS Safari has a third state, `'interrupted'`, for a phone call, Siri, a
    // route change, or the tab going to the background. It is deliberately not
    // resumed here: `resume()` on an interrupted context rejects, so the call
    // would do nothing but swallow its own rejection. What ends an interruption
    // is the interruption ending, and the browser then moves the context to
    // `'running'` on its own or to `'suspended'`, which the line above already
    // picks up.
    //
    // Un-latching readiness on `'interrupted'` to prompt for a gesture is worse
    // than useless for the same reason, and it would tear the meeting down and
    // rebuild it every time an iOS user backgrounds the tab. The remaining case
    // is WebKit 273511, where a context lands in `'interrupted'` and no gesture
    // gets it out; that has no known workaround, and pretending otherwise here
    // would cost the backgrounding case to fix nothing.

    // Nothing built on a closed context makes sound again, so every lease on it
    // is dead whether or not its holder has released yet. Dropping them keeps
    // `activeLeases` reporting holders of the live context rather than ghosts
    // of the last one — a released lease deletes an id that is already gone,
    // which is a no-op. The context itself stays put: `ensureContext` needs it
    // to take this listener back off before it builds the replacement.
    if (state === 'closed') this.leases.clear()

    for (const listener of this.listeners) listener(state)
  }
}

export const audioContextController = new AudioContextController()
